import { useCallback, useEffect, useState } from 'react';
import { ScriptState } from '@/pages/widgetEditor/types';

const ENTRY_SELECTOR = 'script[data-widget-entry]';

// The entry only logs a chunk that fails to load (blocked, 404, bad deploy) —
// no event reaches this page — so a tag still undefined after this long is
// reported as failed rather than left spinning.
export const WIDGET_LOAD_TIMEOUT_MS = 15_000;

/**
 * Loads the selected version's module entry into `frame` (the preview's own
 * window) and reports, per tag, whether the custom element is usable yet.
 *
 * The entry fetches a widget's chunk only once it finds the tag on the page, so
 * readiness is `customElements.whenDefined`, not the script's `load` event —
 * and the preview must render the elements before they are defined, or nothing
 * would ever be discovered.
 *
 * Module scripts are fetched in CORS mode, so this relies on the widgets
 * distribution sending `access-control-allow-origin`, unlike the classic
 * scripts it replaces.
 */
export const useWidgetEntry = (
    entryUrl: string | undefined,
    tags: string[],
    frame: Window | null
): ((tag: string) => ScriptState) => {
    const [failedUrl, setFailedUrl] = useState<string | null>(null);
    const [, setDefinedCount] = useState(0);
    const [timedOutKey, setTimedOutKey] = useState<string | null>(null);

    const needsEntry = Boolean(entryUrl) && tags.length > 0;

    useEffect(() => {
        if (!entryUrl || !needsEntry || !frame) return;

        const { document } = frame;
        let script =
            document.head.querySelector<HTMLScriptElement>(ENTRY_SELECTOR);

        // A remount must not wait forever for an error event that already fired.
        if (script?.dataset.failed) {
            setFailedUrl(entryUrl);
            return;
        }

        if (!script) {
            script = document.createElement('script');
            script.type = 'module';
            script.src = entryUrl;
            script.dataset.widgetEntry = entryUrl;
            document.head.appendChild(script);
        }

        const entryScript = script;
        const handleError = (error: unknown) => {
            console.error(error);
            entryScript.dataset.failed = 'true';
            setFailedUrl(entryUrl);
        };
        entryScript.addEventListener('error', handleError);
        return () => entryScript.removeEventListener('error', handleError);
    }, [entryUrl, needsEntry, frame]);

    const tagKey = Array.from(new Set(tags)).sort().join(' ');

    useEffect(() => {
        if (!frame) return;
        let cancelled = false;
        tagKey
            .split(' ')
            .filter((tag) => tag && !frame.customElements.get(tag))
            .forEach((tag) =>
                frame.customElements.whenDefined(tag).then(() => {
                    if (!cancelled) setDefinedCount((count) => count + 1);
                })
            );
        return () => {
            cancelled = true;
        };
    }, [tagKey, frame]);

    // One timer for the whole selection, restarted whenever it changes.
    useEffect(() => {
        if (!needsEntry) return;
        const timer = setTimeout(
            () => setTimedOutKey(`${entryUrl} ${tagKey}`),
            WIDGET_LOAD_TIMEOUT_MS
        );
        return () => clearTimeout(timer);
    }, [entryUrl, needsEntry, tagKey]);

    return useCallback(
        (tag: string): ScriptState => {
            if (failedUrl === entryUrl) return 'error';
            if (frame?.customElements.get(tag)) return 'ready';
            return timedOutKey === `${entryUrl} ${tagKey}`
                ? 'error'
                : 'loading';
        },
        [failedUrl, entryUrl, frame, timedOutKey, tagKey]
    );
};
